'use client';

/* ──────────────────────────────────────────────
 *  RideVehicleCard — option du sélecteur « Véhicule »
 *  de l'étape trajet.
 *
 *  Tuile COMPACTE (le sélecteur peut lister toute une
 *  flotte : il ne doit pas pousser le formulaire de
 *  trajet hors de l'écran) : petite vignette 3/2, nom et
 *  capacité sur une ligne chacun, tronqués à l'ellipse.
 *  Le contenu long (description, autres photos) vit dans
 *  une modale de détail ouverte par le bouton « i ».
 *
 *  Sert aussi à l'option « Peu importe » (sans visuel,
 *  sans capacité, sans détail) pour que toutes les
 *  options du groupe partagent la même anatomie.
 * ────────────────────────────────────────────── */

import { useEffect, useRef, useState, type JSX, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayPortalTarget } from '../../booking/utils/overlay-portal';
import { Stepper } from '../../booking/components/shared/Stepper';

export interface RideVehicleCardImage {
  preview_url?: string;
  original_url?: string;
}

export interface RideVehicleCardProps {
  name: string;
  /** Description « vitrine » du pro — jamais dans la tuile, seulement dans la modale de détail. */
  description?: string;
  /** Capacité DÉJÀ formatée (passagers/bagages). Vide = ligne masquée. */
  capacity?: string;
  /** Visuels déjà signés par le backend. Vide/absent = tuile sans vignette (pas de placeholder). */
  pictures?: RideVehicleCardImage[];
  selected: boolean;
  onSelect: () => void;
  labels: { details: string; close: string; choose: string };
  /**
   * Mode MULTI-VÉHICULES : sélecteur de quantité (0..max) au lieu du choix exclusif. La tuile
   * cesse d'être un `radio` (plusieurs classes peuvent être retenues en même temps) et n'est plus
   * cliquable — le stepper est le seul contrôle, pour ne pas mettre deux commandes concurrentes
   * sur la même cible. Absent = comportement mono historique, strictement inchangé.
   */
  quantity?: {
    value: number;
    max: number;
    onChange: (next: number) => void;
    labels: { decrease: string; increase: string };
  };
}

const pickThumb = (image: RideVehicleCardImage): string | undefined => image.preview_url ?? image.original_url;
const pickLarge = (image: RideVehicleCardImage): string | undefined => image.original_url ?? image.preview_url;

export function RideVehicleCard({
  name,
  description,
  capacity,
  pictures,
  selected,
  onSelect,
  labels,
  quantity,
}: RideVehicleCardProps): JSX.Element {
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const images = (pictures ?? []).filter((picture): boolean => Boolean(pickThumb(picture)));
  const isDetailOpen = detailIndex !== null;
  const tileRef = useRef<HTMLDivElement | null>(null);
  const detailPortalTarget = useOverlayPortalTarget(tileRef, isDetailOpen);
  // Le détail n'a d'intérêt que s'il montre plus que la tuile : description, ou photo(s) en grand.
  const hasDetail = Boolean(description) || images.length > 0;

  // Clavier : la tuile est un `radio` (choix exclusif) — Entrée et Espace la sélectionnent,
  // Espace est bloqué pour ne pas scroller la page.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect();
  };

  useEffect((): (() => void) | undefined => {
    if (!isDetailOpen) return undefined;
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') setDetailIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [isDetailOpen]);

  const stop = (event: MouseEvent): void => event.stopPropagation();
  const current = detailIndex !== null ? images[detailIndex] : undefined;
  const largeSrc = current ? pickLarge(current) : undefined;

  return (
    <>
      <div
        ref={tileRef}
        {...(quantity
          ? { role: 'group', 'aria-label': name }
          : { role: 'radio', 'aria-checked': selected, tabIndex: 0, onClick: onSelect, onKeyDown: handleKeyDown })}
        className={`bt-vehicle-tile ${selected ? 'bt-vehicle-tile--selected' : ''}`}
      >
        {images.length > 0 ? (
          <img className="bt-vehicle-tile__thumb" src={pickThumb(images[0])} alt="" loading="lazy" />
        ) : null}
        <div className="bt-vehicle-tile__info">
          <span className="bt-vehicle-tile__name">{name}</span>
          {capacity ? <span className="bt-vehicle-tile__meta">{capacity}</span> : null}
        </div>
        {quantity ? (
          <Stepper
            className="bt-vehicle-tile__stepper"
            value={quantity.value}
            onChange={quantity.onChange}
            min={0}
            max={quantity.max}
            labels={{
              decrease: `${quantity.labels.decrease} — ${name}`,
              increase: `${quantity.labels.increase} — ${name}`,
            }}
          />
        ) : null}
        {hasDetail ? (
          /* Ouvrir le détail ne sélectionne pas le véhicule (stopPropagation) : le client
             peut comparer les fiches avant de choisir. */
          <button
            type="button"
            className="bt-vehicle-tile__details"
            aria-label={`${labels.details} — ${name}`}
            onClick={(event): void => {
              event.stopPropagation();
              setDetailIndex(0);
            }}
          >
            <InfoIcon />
          </button>
        ) : null}
      </div>

      {isDetailOpen &&
        detailPortalTarget &&
        createPortal(
          /* Portal HORS de `.bt-root` : la racine déclare `container-type: inline-size`, qui
             ferait d'elle le containing block d'un backdrop `position: fixed` (voile partiel et
             centrage cassé) — même contrainte que la lightbox et la modale vidéo. Cible résolue
             par `useOverlayPortalTarget` : <body>, ou la racine du shadow root dans le widget
             embarqué (sinon la modale sortirait de sa feuille de style). */
          <div
            className="bt-vehicle-modal__backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={name}
            onClick={(): void => setDetailIndex(null)}
          >
            <div className="bt-vehicle-modal" onClick={stop}>
              <div className="bt-vehicle-modal__header">
                <span className="bt-vehicle-modal__title">{name}</span>
                <button
                  type="button"
                  className="bt-vehicle-modal__close"
                  aria-label={labels.close}
                  onClick={(): void => setDetailIndex(null)}
                >
                  ✕
                </button>
              </div>

              {largeSrc ? <img className="bt-vehicle-modal__img" src={largeSrc} alt={name} /> : null}

              {images.length > 1 && (
                <div className="bt-vehicle-modal__thumbs">
                  {images.map((picture, index): JSX.Element => {
                    const isCurrent = index === detailIndex;
                    return (
                      <button
                        key={pickThumb(picture) ?? index}
                        type="button"
                        aria-label={`${name} ${index + 1}/${images.length}`}
                        aria-current={isCurrent}
                        className={`bt-vehicle-modal__thumb ${isCurrent ? 'bt-vehicle-modal__thumb--current' : ''}`}
                        onClick={(): void => setDetailIndex(index)}
                      >
                        <img src={pickThumb(picture)} alt="" />
                      </button>
                    );
                  })}
                </div>
              )}

              {capacity ? <p className="bt-vehicle-modal__meta">{capacity}</p> : null}
              {description ? <p className="bt-vehicle-modal__desc">{description}</p> : null}

              <button
                type="button"
                className="bt-cta bt-cta--small"
                onClick={(): void => {
                  onSelect();
                  setDetailIndex(null);
                }}
              >
                {labels.choose}
              </button>
            </div>
          </div>,
          detailPortalTarget,
        )}
    </>
  );
}

function InfoIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}
